/**
 * Native `fetch` / `AbortController` abort detection.
 *
 * `fetch` and `AbortController` reject with a `DOMException` whose `name` is
 * `"AbortError"` and nothing else. {@link fetchWithRetry}'s retry/timeout loop
 * relies on this narrow shape: only a genuine `fetch` cancellation should skip
 * the transient-error branch, so it must NOT treat a plain `Error` that merely
 * happens to be named `"AbortError"` as a cancellation. Keep this strict to
 * preserve that behavior — see {@link isAbortError} for the broader variant.
 *
 * @param err - The caught value to classify.
 * @returns `true` when `err` is a native `DOMException` abort.
 */
export function isDomAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/**
 * Broad cancellation-error detection.
 *
 * `fetch` / `AbortController` reject with a `DOMException` named `"AbortError"`,
 * but some call sites synthesize (or re-wrap into) a plain `Error` with the same
 * name. Tool executors and the executor runner deliberately treat both as a
 * cancellation so an aborted tool call unwinds the surrounding loop regardless
 * of which abort shape bubbles up.
 *
 * Prefer {@link isDomAbortError} when you must match only native `fetch` aborts
 * (e.g. inside the HTTP retry loop, where the plain-`Error` case has different
 * semantics).
 *
 * @param err - The caught value to classify.
 * @returns `true` when `err` represents a cancellation/abort.
 */
export function isAbortError(err: unknown): boolean {
  return (
    isDomAbortError(err) || (err instanceof Error && err.name === "AbortError")
  );
}
